import { supabase } from '../lib/supabase/client'

interface ReportSequence {
  id: string
  prefix: string
  current_number: number
  format_pattern: string
  description?: string
  created_at: string
  updated_at: string
}

export class ReportNumberingService {
  /**
   * Get the next report number for a given prefix (increments the sequence)
   * This should be called when actually creating/finalizing a report
   */
  static async getNextReportNumber(prefix: string): Promise<string> {
    try {
      const { data, error } = await supabase.rpc('get_next_report_number', {
        sequence_prefix: prefix
      })

      if (error) {
        console.error('Error getting next report number:', error)
        throw new Error(`Failed to get next report number: ${error.message}`)
      }

      return data as string
    } catch (error) {
      console.error('Error in getNextReportNumber:', error)
      throw error
    }
  }

  /**
   * Preview the next report number without incrementing the sequence
   * This is useful for UI display before actually creating the report
   */
  static async peekNextReportNumber(prefix: string): Promise<string> {
    try {
      const { data, error } = await supabase.rpc('peek_next_report_number', {
        sequence_prefix: prefix
      })

      if (error) {
        console.error('Error peeking next report number:', error)
        throw new Error(`Failed to peek next report number: ${error.message}`)
      }

      return data as string
    } catch (error) {
      console.error('Error in peekNextReportNumber:', error)
      throw error
    }
  }

  /**
   * Get all available report sequences
   */
  static async getReportSequences(): Promise<ReportSequence[]> {
    try {
      const { data, error } = await supabase
        .from('report_sequences')
        .select('*')
        .order('prefix')

      if (error) {
        console.error('Error fetching report sequences:', error)
        throw new Error(`Failed to fetch report sequences: ${error.message}`)
      }

      return data || []
    } catch (error) {
      console.error('Error in getReportSequences:', error)
      throw error
    }
  }

  /**
   * Create a new report sequence
   */
  static async createReportSequence(
    prefix: string,
    startingNumber: number = 0,
    formatPattern: string = `${prefix}-{number:05d}`,
    description?: string
  ): Promise<ReportSequence> {
    try {
      const { data, error } = await supabase
        .from('report_sequences')
        .insert({
          prefix,
          current_number: startingNumber,
          format_pattern: formatPattern,
          description
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating report sequence:', error)
        throw new Error(`Failed to create report sequence: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error in createReportSequence:', error)
      throw error
    }
  }

  /**
   * Update a report sequence (admin only)
   */
  static async updateReportSequence(
    id: string,
    updates: Partial<Pick<ReportSequence, 'current_number' | 'format_pattern' | 'description'>>
  ): Promise<ReportSequence> {
    try {
      const { data, error } = await supabase
        .from('report_sequences')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('Error updating report sequence:', error)
        throw new Error(`Failed to update report sequence: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error in updateReportSequence:', error)
      throw error
    }
  }

  /**
   * Reset a report sequence to a specific number (admin only - use with caution)
   */
  static async resetReportSequence(prefix: string, newNumber: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('report_sequences')
        .update({ current_number: newNumber })
        .eq('prefix', prefix)

      if (error) {
        console.error('Error resetting report sequence:', error)
        throw new Error(`Failed to reset report sequence: ${error.message}`)
      }
    } catch (error) {
      console.error('Error in resetReportSequence:', error)
      throw error
    }
  }

  /**
   * Get report number for an inspection record
   */
  static async getInspectionReportNumber(inspectionId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('inspection_records')
        .select('report_number')
        .eq('id', inspectionId)
        .single()

      if (error) {
        console.error('Error fetching inspection report number:', error)
        return null
      }

      return data?.report_number || null
    } catch (error) {
      console.error('Error in getInspectionReportNumber:', error)
      return null
    }
  }

  /**
   * Manually assign a report number to an inspection (useful for migration or manual assignment)
   */
  static async assignReportNumberToInspection(
    inspectionId: string,
    reportNumber?: string
  ): Promise<string> {
    try {
      let finalReportNumber = reportNumber

      // If no report number provided, generate one
      if (!finalReportNumber) {
        finalReportNumber = await this.getNextReportNumber('STMI')
      }

      const { error } = await supabase
        .from('inspection_records')
        .update({ report_number: finalReportNumber })
        .eq('id', inspectionId)

      if (error) {
        console.error('Error assigning report number to inspection:', error)
        throw new Error(`Failed to assign report number: ${error.message}`)
      }

      return finalReportNumber
    } catch (error) {
      console.error('Error in assignReportNumberToInspection:', error)
      throw error
    }
  }
} 